import { call, put, delay } from "redux-saga/effects";
import MessageConstant from '../../utils/Constant/MessageConstant';
import {
  createUser,
  deleteUser,
  getUser,
  loginUser,
  logoutUser,
  refreshUser,
  updateUser,
  forgotPassword,
  resetPassword,
  resendConfirmation,
  uploadAvatar,
  cookieStatus,
} from "../actions/userActionsHelper";
import { setLoading } from "../reducers/userReducersHelper";
import { resetQuestionState } from "../reducers/questionReducersHelper";
import { resetRecordState } from "../reducers/recordReducersHelper";
import { resetReceiptState } from "../reducers/receiptReducersHelper";
import { resetPackageState } from "../reducers/packageReducersHelper";
import * as userApi from '../../api/index';
import { setAccessToken, clearAccessToken } from '../../api/config';

export function* handleGetUsers() {
  try {
    yield put(setLoading(true));
    const users = yield call(userApi.getUsers);
    yield put(getUser.success(users?.data));
  } catch (err) {
    yield put(getUser.failed(err.message || 'Failed to fetch users.'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleLogin(action) {
  try {
    // yield put(setLoading(true));
    const response = yield call(userApi.signIn, action.payload);

    // Dispatch cookie sent status
    yield put(cookieStatus.setSent());

    yield put(loginUser.success(response.data.user));

    // Store access token in memory (not localStorage)
    if (response.data?.accessToken) {
      setAccessToken(response.data.accessToken);
    }

    // Wait for browser to write cookie, then confirm
    yield delay(400);
    yield put(cookieStatus.setConfirmed());

    // Refresh token is automatically stored in httpOnly cookie by backend
  } catch (err) {
    yield put(
      loginUser.failed(
        err.response?.data?.error || err.message || "Login failed. Please check your credentials."
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleLogout() {
  try {
    // Call logout API to destroy session
    yield call(userApi.logout);

    // Clear access token from memory
    clearAccessToken();

    // Clear old localStorage tokens if they exist
    localStorage.removeItem("Token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    // Clear all Redux state
    yield put(resetQuestionState());
    yield put(resetRecordState());
    yield put(resetReceiptState());
    yield put(resetPackageState());

    // Clear session/local storage if needed
    localStorage.removeItem("userSectionStats");
    sessionStorage.clear();

    yield put(logoutUser.success());
  } catch (err) {
    // Even if logout API fails, clear local state
    clearAccessToken();
    localStorage.removeItem("Token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    yield put(logoutUser.failed(err.message || 'Logout failed.'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleRefreshUser(action) {
  try {
    const response = yield call(userApi.refreshToken);

    if (response.data.success && response.data.accessToken) {
      // Set access token in memory
      setAccessToken(response.data.accessToken);

      // Update Redux state with user data if returned
      if (response.data.user) {
        yield put(refreshUser.success({ userData: response.data.user }));
      } else {
        yield put(refreshUser.success({ userData: null }));
      }
    } else {
      yield put(refreshUser.failed('Token refresh failed'));
    }
  } catch (err) {
    const { retryCount = 0, maxRetries = 4 } = action.payload || {};
    const isNoCookie = err.response?.data?.code === 'NO_REFRESH_TOKEN';

    // If cookie not found and retries remaining, dispatch retry
    if (isNoCookie && retryCount < maxRetries) {
      const retryDelays = [100, 200, 300, 500];
      const delay = retryDelays[retryCount] || 500;
      yield call(delay => new Promise(resolve => setTimeout(resolve, delay)), delay);
      yield put(refreshUser.request({ retryCount: retryCount + 1, maxRetries }));
    } else {
      yield put(refreshUser.failed(err.response?.data?.message || err.message || MessageConstant.SESSION_EXPIRED_GENERIC));
    }
  }
}

export function* handleCreateUser(action) {
  try {
    yield put(setLoading(true));
    const response = yield call(userApi.signUp, action.payload);
    yield put(createUser.success(response.data));
  } catch (err) {
    yield put(createUser.failed(err.response?.data?.error || err.message || 'Create user failed.'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleUpdateUser(action) {
  try {
    yield put(setLoading(true));
    const { id, userData } = action.payload;
    const response = yield call(userApi.updateUser, id, userData);
    // Backend returns [user] array, extract the user object
    const updatedUser = Array.isArray(response.data) ? response.data[0] : response.data;
    yield put(updateUser.success(updatedUser));
  } catch (err) {
    yield put(updateUser.failed(err.response?.data?.error || err.message || 'Update user failed.'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleDeleteUser(action) {
  try {
    yield put(setLoading(true));
    yield call(userApi.deleteUser, action.payload);
    yield put(deleteUser.success(action.payload));
  } catch (err) {
    yield put(deleteUser.failed(err.response?.data?.error || err.message || 'Delete user failed.'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleForgotPassword(action) {
  try {
    const response = yield call(userApi.forgotPassword, action.payload);
    yield put(forgotPassword.success(response.data));
  } catch (err) {
    yield put(
      forgotPassword.failed(
        err.response?.data?.error || err.message || "Password reset failed. Please try again."
      )
    );
  }
}

export function* handleResetPassword(action) {
  try {
    const response = yield call(userApi.resetPassword, action.payload);
    yield put(resetPassword.success(response.data));
  } catch (err) {
    yield put(
      resetPassword.failed(
        err.response?.data?.error || err.message || "Password reset failed. Please try again."
      )
    );
  }
}

export function* handleResendConfirmation(action) {
  try {
    const response = yield call(userApi.resendConfirmation, action.payload);
    yield put(resendConfirmation.success(response.data));
  } catch (err) {
    yield put(
      resendConfirmation.failed(
        err.response?.data?.error || err.message || "Failed to resend confirmation email. Please try again."
      )
    );
  }
}

export function* handleUploadAvatar(action) {
  try {
    const response = yield call(userApi.uploadAvatar, action.payload);
    yield put(uploadAvatar.success(response.data.url));
  } catch (err) {
    yield put(
      uploadAvatar.failed(
        err.response?.data?.error || err.message || "Avatar upload failed. Please try again."
      )
    );
  }
}
